import {
  action, computed, makeObservable, observable, runInAction,
} from 'mobx';
import { nanoid } from 'nanoid';
import { FeatureValue } from '@mfe-4cs/types/comsApi';
import { TefComs } from '@mfe-4cs/types/typings';
import Service from '@mfe-4cs/util/Service';

export class FeatureValueStore implements FeatureValue {
  private $fid: string;

  changes: number = 0;
  description: string;
  granted?: boolean = undefined;
  id: string;
  title: string;
  modified = false;
  type: string = 'FeatureValueStore';

  private service;

  constructor({
    id, description, title, granted,
  }: FeatureValue) {
    makeObservable(this, {
      changes: observable,
      granted: observable,
      setGranted: action,
      setModified: action,
      modified: observable,
      isRevokedOrGranted: computed,
    });
    this.id = id;
    this.description = description;
    this.title = title;
    this.granted = granted;
    this.$fid = nanoid();
    this.service = new Service();
  }

  change(value: boolean): void {
    if (value !== this.granted) {
      this.setGranted(value);
      this.setModified(true);
      this.triggerChange();
    }
  }

  setGranted(granted: boolean): void {
    this.granted = granted;
  }

  setModified(modified: boolean): void {
    this.modified = modified;
  }

  get fid(): string {
    return this.$fid;
  }

  /**
   * Determines if the feature has been either explicitly granted or revoked.
   *
   * @return {boolean} True if the 'granted' property is defined (either granted or revoked); false otherwise.
   */
  get isRevokedOrGranted() {
    return typeof this.granted !== 'undefined';
  }

  public toJSON(): FeatureValue {
    return {
      ...this,
    };
  }

  public toSaveFeatureValue(): TefComs.ShortFeatureValue {
    const { id, granted = false } = this;
    return { id, granted };
  }

  public toShortFeatureValue(): TefComs.ShortFeatureValue {
    return this.toSaveFeatureValue();
  }

  public triggerChange(): void {
    runInAction(() => {
      this.changes += 1;
    });
    this.service.fireEvent<TefComs.EventDetails>({
      name: 'consents/CHANGE',
      level: 'info',
      message: 'user changed a consent',
    });
  }
}
